// Transcripts API self-serve affordances. The docs guide link and copyable LLM
// prompt live here so the upload surface stays thin. In-app hyperlinks to
// docs.usemarker.ai are allowed for product-access holders.
//
// The prompt text below is fixed product copy; update it only through the
// owning product-review path.

import { getRuntimeConfig } from "../config";
import { docUrl, docsOrigin } from "./docLinks";

export const TRANSCRIPT_SUBMIT_GUIDE_URL = docUrl("guides/submit-a-transcript");

export function transcriptApiLlmPrompt(): string {
  const baseUrl = getRuntimeConfig().publicBaseUrl || window.location.origin;
  return `You are helping me integrate my application with Marker's transcript-upload API.
Write client code that submits conversation transcripts (text, optionally with audio)
to Marker for evaluation. Follow this contract exactly — do not invent fields.

Base URL: ${baseUrl}
Auth: every request needs the header  Authorization: Bearer <MARKER_API_KEY>
(machine API key with the transcripts:write scope).
Work-launching POSTs also require an  Idempotency-Key: <unique string>  header
(generate a fresh UUID per logical submission; reuse it only when retrying that
same submission).

FLOW 1 — text-only transcript (single call):
POST /v1/transcripts            (headers: Authorization, Idempotency-Key, Content-Type: application/json)
Body (minimal valid):
{
  "agent_id": "<agent UUID>",
  "version_tag": "<non-empty version label, e.g. v1>",
  "utterances": [
    { "role": "user", "text": "Hi, I need to change my order." },
    { "role": "assistant", "text": "Sure - what would you like to change?" }
  ]
}
- utterances is required for text-only ingest and must be non-empty; keep them in time order.
- role is one of: "user" | "assistant" | "tool".
- Optional per-utterance fields: speaker (string), start_ms / end_ms (integer milliseconds).
- Optional top-level fields: include_marker_ids, suppress_marker_ids (arrays of marker UUIDs).
- The schema rejects unknown fields (additionalProperties: false).
201 response includes: id (transcript UUID), status ("pending"|"processing"|"ready"|"failed"),
created_at, ... Poll GET /v1/transcripts/{id} until status is "ready" or "failed".

FLOW 2 — transcript with audio (3 steps; never send audio bytes to the API itself):
1) POST /v1/transcripts  (headers as above) with body:
{
  "agent_id": "<agent UUID>",
  "version_tag": "<version label>",
  "audio_intent": "presigned_put",
  "audio_content_type": "audio/wav"
}
   audio_content_type must be exactly "audio/wav" when audio_intent is "presigned_put".
   utterances is optional here (may be omitted; if supplied, must be non-empty).
   Keep the returned id.
2) POST /v1/transcripts/{id}:create-audio-upload-url   (Authorization header only; empty body)
   200 response: { "url", "method" (default "PUT"), "required_headers" (object of
   header-name -> value), "max_bytes", "expires_at" }.
   Upload the raw WAV bytes: send an HTTP request with that method to that url,
   setting EXACTLY the headers in required_headers (echo them verbatim; add nothing else).
   The body must be <= max_bytes and the upload must finish before expires_at.
3) POST /v1/transcripts/{id}:complete-audio-upload  (headers: Authorization, Idempotency-Key; empty body)
   Success is 202 Accepted with no body. Then poll GET /v1/transcripts/{id} as above.

Alternative to FLOW 2: audio_intent "pull_url" + audio_source_url lets Marker fetch the
audio itself (audio_content_type must be omitted in that mode).

Errors: non-2xx responses return { code, message, request_id, retryable, retry_after? } -
retry only when retryable is true, honoring retry_after (seconds) if present.

Full schemas: ${docsOrigin()}/docs/api-reference/transcripts/transcriptsCreate ,
.../transcriptsCreateAudioUploadUrl , .../transcriptsCompleteAudioUpload`;
}
